import type { PageLoadInput } from "./page-load";
import type { Metadata } from "./page-metadata";

type PageModule<Data> = {
  loadPage: (options: { data: PageLoadInput }) => Promise<Data>;
  metadata?: Metadata;
};

const failures = new WeakMap<Error, { route: string; stage: "module_import" | "server_function" }>();

export function pageLoadFailure(error: unknown) {
  return error instanceof Error ? failures.get(error) : undefined;
}

/** Keep page modules out of the eager route tree, including their metadata. */
export async function loadRoutePage<Data>(
  importPage: () => Promise<PageModule<Data>>,
  input: PageLoadInput,
  route = "unknown",
) {
  let stage: "module_import" | "server_function" = "module_import";
  try {
    const page = await importPage();
    stage = "server_function";
    // The owning page's validated, guarded server function remains authoritative.
    const data = await page.loadPage({ data: input });
    const dynamicMetadata = data && typeof data === "object" && "metadata" in data
      ? data.metadata as Metadata | null | undefined
      : undefined;
    return { data, metadata: dynamicMetadata ?? page.metadata ?? {} };
  } catch (error) {
    // Generated route IDs contain templates, never the URL's private values.
    // Keep redirects and the original error unchanged; the page boundary reports.
    if (error instanceof Error) failures.set(error, { route, stage });
    throw error;
  }
}
